import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import { EnableFeature as LabsEnable } from '~/Common-Objects/Labs/enableFeature'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import PostHog from '~/Classes/posthog'

const AUTOPLAY_MS = 5000

const NEWS_ITEMS = [
    {
        id: 'mcp',
        eyebrow: 'dashboard:whats-new.items.mcp.eyebrow',
        title: 'dashboard:whats-new.items.mcp.title',
        body: 'dashboard:whats-new.items.mcp.body',
        cta: 'dashboard:whats-new.items.mcp.cta',
        action: 'enableLab',
        labName: 'transpond-mcp',
        fallbackTo: '/cp/labs',
        showNew: true
    },
    {
        id: 'landing-pages',
        eyebrow: 'dashboard:whats-new.items.landing-pages.eyebrow',
        title: 'dashboard:whats-new.items.landing-pages.title',
        body: 'dashboard:whats-new.items.landing-pages.body',
        cta: 'dashboard:whats-new.items.landing-pages.cta',
        to: '/cp/landingpages',
        showNew: true
    },
    {
        id: 'social-posting',
        eyebrow: 'dashboard:whats-new.items.social-posting.eyebrow',
        title: 'dashboard:whats-new.items.social-posting.title',
        body: 'dashboard:whats-new.items.social-posting.body',
        cta: 'dashboard:whats-new.items.social-posting.cta',
        href: 'https://transpond.io/support/creating-and-sending-campaigns/social-campaigns',
        external: true
    }
]

function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function trackWhatsNew(event, data = {}) {
    PostHog.capture(event, data)
}

function NewsCta({ item, onEnableLab }) {
    function onCtaClick() {
        trackWhatsNew('whats_new_cta_clicked', {
            item: item.id,
            destination: item.action || (item.external ? item.href : item.to),
            external: !!item.external
        })
        if (item.action === 'enableLab') {
            onEnableLab(item)
        }
    }

    const shared = {
        className: 'button-sml frontpage-whats-new__cta',
        iconRight: <Icons icon='arrowRight' />,
        onClick: onCtaClick,
        text: i18n.t(item.cta)
    }

    if (item.action === 'enableLab') {
        return <Button type='button' {...shared} />
    }

    if (item.external) {
        return <Button {...shared} href={item.href} target='_blank' />
    }

    return <Button {...shared} to={item.to} />
}

export default function WhatsNew() {
    const history = useHistory()
    const [index, setIndex] = useState(0)
    const [autoplay, setAutoplay] = useState(() => !prefersReducedMotion())
    const [hovered, setHovered] = useState(false)
    const [overlay, setOverlay] = useState(undefined)
    const total = NEWS_ITEMS.length

    useEffect(() => {
        trackWhatsNew('whats_new_viewed', {
            items: NEWS_ITEMS.map((item) => item.id)
        })
    }, [])

    useEffect(() => {
        if (!autoplay || hovered) return undefined

        const timer = setTimeout(() => {
            if (index >= total - 1) {
                setAutoplay(false)
                return
            }
            setIndex((current) => current + 1)
        }, AUTOPLAY_MS)

        return () => clearTimeout(timer)
    }, [autoplay, hovered, index, total])

    function goTo(nextIndex, method) {
        const target = ((nextIndex % total) + total) % total
        trackWhatsNew('whats_new_navigated', {
            method,
            from: NEWS_ITEMS[index].id,
            to: NEWS_ITEMS[target].id
        })
        setAutoplay(false)
        setIndex(target)
    }

    async function openEnableLab(item) {
        const fallback = item.fallbackTo || '/cp/labs'
        try {
            const response = await axios.get('/accountmaster/labs')
            const lab = (response.data || []).find((entry) => entry.name === item.labName)
            if (!lab) {
                history.push(fallback)
                return
            }
            setOverlay(
                <LabsEnable
                    labId={lab.id}
                    onClose={() => {
                        setOverlay(undefined)
                    }}
                />
            )
        } catch (err) {
            history.push(fallback)
        }
    }

    return (
        <Paper className='frontpage-whats-new m-mar-b0'>
            <h4 className='mar-b5'>{i18n.t('dashboard:whats-new.title')}</h4>
            <div className='frontpage-whats-new__subtitle mar-b15'>{i18n.t('dashboard:whats-new.subtitle')}</div>

            <div
                className='frontpage-whats-new__body'
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className='frontpage-whats-new__viewport'>
                    <div className='frontpage-whats-new__slides' style={{ transform: `translateX(-${index * 100}%)` }}>
                        {NEWS_ITEMS.map((newsItem) => (
                            <div className='frontpage-whats-new__slide' key={newsItem.id}>
                                <div className='frontpage-whats-new__card'>
                                    <div className='frontpage-whats-new__eyebrow'>{i18n.t(newsItem.eyebrow)}</div>
                                    <h5 className='frontpage-whats-new__heading'>
                                        {i18n.t(newsItem.title)}
                                        {newsItem.showNew && (
                                            <span className='frontpage-whats-new__pill'>
                                                {i18n.t('dashboard:whats-new.new')}
                                            </span>
                                        )}
                                    </h5>
                                    <p className='frontpage-whats-new__copy'>{i18n.t(newsItem.body)}</p>
                                    <NewsCta item={newsItem} onEnableLab={openEnableLab} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='frontpage-whats-new__chrome'>
                    <div className='frontpage-whats-new__dots' role='tablist'>
                        {NEWS_ITEMS.map((newsItem, i) => (
                            <button
                                key={newsItem.id}
                                type='button'
                                className={
                                    'frontpage-whats-new__dot' +
                                    (i === index ? ' frontpage-whats-new__dot--active' : '')
                                }
                                aria-label={i18n.t('dashboard:whats-new.go-to-item', {
                                    n: i + 1
                                })}
                                aria-selected={i === index}
                                onClick={() => goTo(i, 'dot')}
                            />
                        ))}
                    </div>
                    <div className='frontpage-whats-new__nav'>
                        <button
                            type='button'
                            className='frontpage-whats-new__nav-btn'
                            aria-label={i18n.t('dashboard:whats-new.previous')}
                            onClick={() => goTo(index - 1, 'prev')}
                        >
                            <Icons icon='chevronLeft' />
                        </button>
                        <button
                            type='button'
                            className='frontpage-whats-new__nav-btn'
                            aria-label={i18n.t('dashboard:whats-new.next')}
                            onClick={() => goTo(index + 1, 'next')}
                        >
                            <Icons icon='chevronRight' />
                        </button>
                    </div>
                </div>

                <div
                    className={
                        'frontpage-whats-new__progress' + (!autoplay ? ' frontpage-whats-new__progress--done' : '')
                    }
                >
                    {autoplay && !hovered && (
                        <i
                            key={`${index}-${autoplay}`}
                            className='frontpage-whats-new__progress-bar frontpage-whats-new__progress-bar--run'
                        />
                    )}
                </div>
            </div>
            {overlay}
        </Paper>
    )
}
